import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { UNITS_OVERVIEW_PANELS, UNIT_LABEL_SHORT } from '../constants/unitsMeta';

function StatLine({ label, value }) {
  return (
    <div className="units-overview-stat">
      <span className="units-overview-stat__label">{label}</span>
      <span className="units-overview-stat__value">{value}</span>
    </div>
  );
}

function applyOverviewToState(res, setData, setEditorInputs) {
  setData(res);
  if (res.applicationEditorByUnit) {
    setEditorInputs({
      mp: res.applicationEditorByUnit.mp || '',
      cid: res.applicationEditorByUnit.cid || '',
      qrf: res.applicationEditorByUnit.qrf || '',
    });
  }
}

export default function UnitsOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorInputs, setEditorInputs] = useState({ mp: '', cid: '', qrf: '' });
  const [savingEditor, setSavingEditor] = useState(null);
  const [formSchemaDrafts, setFormSchemaDrafts] = useState({ mp: '', cid: '', qrf: '' });
  const [formSchemaLoadError, setFormSchemaLoadError] = useState(null);
  const [savingFormSchema, setSavingFormSchema] = useState(null);
  const navigate = useNavigate();
  const { error: toastError, success } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api-me-overview');
        if (!cancelled) applyOverviewToState(res, setData, setEditorInputs);
      } catch (e) {
        if (e.status === 403) {
          toastError('Nie masz dostępu do podglądu wszystkich jednostek.');
          navigate('/jednostki', { replace: true });
        } else if (e.status === 401) {
          navigate('/', { replace: true });
        } else {
          toastError(e.message || 'Nie udało się załadować podglądu');
          navigate('/jednostki', { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, toastError]);

  useEffect(() => {
    if (!data?.canAssignApplicationEditors) return undefined;
    let cancelled = false;
    setFormSchemaLoadError(null);
    (async () => {
      try {
        const next = { mp: '', cid: '', qrf: '' };
        for (const u of ['mp', 'cid', 'qrf']) {
          const d = await api.get(`/admin-application-form-schema?unit=${u}`);
          next[u] = JSON.stringify(d.schema, null, 2);
        }
        if (!cancelled) setFormSchemaDrafts(next);
      } catch (e) {
        if (!cancelled) {
          setFormSchemaLoadError(e.message || 'Nie udało się wczytać szablonów formularzy');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data?.canAssignApplicationEditors]);

  const handleSaveApplicationEditor = async (u) => {
    setSavingEditor(u);
    try {
      await api.put('/admin-unit-application-editor', {
        unit: u,
        editor_discord_id: editorInputs[u] ?? '',
      });
      success(`Zapisano edytora treści podań: ${UNIT_LABEL_SHORT[u] || u}`);
      const res = await api.get('/api-me-overview');
      applyOverviewToState(res, setData, setEditorInputs);
    } catch (err) {
      toastError(err.message || 'Nie udało się zapisać edytora');
    } finally {
      setSavingEditor(null);
    }
  };

  const handleSaveFormSchema = async (u) => {
    setSavingFormSchema(u);
    try {
      let parsed;
      try {
        parsed = JSON.parse(formSchemaDrafts[u] || '{}');
      } catch {
        toastError('Nieprawidłowy JSON — sprawdź składnię.');
        return;
      }
      await api.put('/admin-application-form-schema', { unit: u, schema: parsed });
      success(`Zapisano szablon formularza: ${UNIT_LABEL_SHORT[u] || u}`);
      const d = await api.get(`/admin-application-form-schema?unit=${u}`);
      setFormSchemaDrafts((prev) => ({ ...prev, [u]: JSON.stringify(d.schema, null, 2) }));
      setFormSchemaLoadError(null);
    } catch (err) {
      toastError(err.message || 'Nie udało się zapisać szablonu');
    } finally {
      setSavingFormSchema(null);
    }
  };

  if (loading) {
    return (
      <div className="form-page unit-select-page">
        <div className="form-container unit-overview-loading">Ładowanie podglądu…</div>
      </div>
    );
  }

  if (!data) return null;

  const { user, units, canAssignApplicationEditors } = data;

  return (
    <div className="form-page unit-select-page">
      <div className="form-container unit-overview-container">
        <header className="unit-overview-top">
          <Link to="/jednostki" className="unit-switch-bar__link unit-overview-back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Wróć do wyboru jednostki
          </Link>
          <div className="unit-overview-hero">
            <img
              src={user.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'}
              alt=""
              className="unit-overview-avatar"
            />
            <div>
              <h1 className="unit-overview-title">Podgląd wszystkich jednostek</h1>
              <p className="unit-overview-sub">
                Witaj, <strong>{user.username}</strong> — skrót dla Military Police, Criminal Investigation Division (CID)
                oraz QRF (quick reaction force) naraz.
              </p>
              <p className="units-overview-stats-legend">
                Liczniki <strong>podań</strong> i <strong>skarg</strong> w każdej kolumnie to suma dla <strong>całej jednostki</strong>{' '}
                (wszyscy użytkownicy), a nie tylko Twoje własne zgłoszenia. Blacklista nadal dotyczy Twojego konta.
              </p>
            </div>
          </div>
        </header>

        {canAssignApplicationEditors && (
          <div className="units-overview-editor-assigner">
            <h3 className="units-overview-editor-assigner__title">Edytorzy treści podań (jedna osoba na jednostkę)</h3>
            <p className="units-overview-editor-assigner__desc">
              Tylko wyznaczone Discord ID mogą edytować treść podań ze statusem „oczekujące”. Akceptacja i odrzucenie
              nadal należy do administracji danej jednostki w panelu admin.
            </p>
            <div className="units-overview-editor-assigner__rows">
              {['mp', 'cid', 'qrf'].map((u) => (
                <div key={u} className="units-overview-editor-assigner__row">
                  <span className="units-overview-editor-assigner__unit">{UNIT_LABEL_SHORT[u]}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Discord ID (puste = wyłączone)"
                    value={editorInputs[u] || ''}
                    onChange={(e) => setEditorInputs((prev) => ({ ...prev, [u]: e.target.value }))}
                    className="admin-modal__input units-overview-editor-assigner__input"
                  />
                  <button
                    type="button"
                    className="admin-table-btn admin-table-btn--primary"
                    disabled={savingEditor === u}
                    onClick={() => handleSaveApplicationEditor(u)}
                  >
                    {savingEditor === u ? 'Zapisywanie…' : 'Zapisz'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {canAssignApplicationEditors && (
          <div className="units-overview-editor-assigner units-overview-form-schema">
            <h3 className="units-overview-editor-assigner__title">Szablony formularzy podań (JSON)</h3>
            <p className="units-overview-editor-assigner__desc">
              Struktura: <code>embed_title</code>, <code>embed_description</code>, tablica <code>fields</code> (id, label,
              type: text | textarea | number | select, required, …). Pole <code>ic_name</code> jest wymagane. Maks. ok. 22
              pola (limit pól w embedzie Discord).
            </p>
            {formSchemaLoadError && (
              <p className="units-overview-form-schema__error" role="alert">
                {formSchemaLoadError}
              </p>
            )}
            <div className="units-overview-form-schema__grid">
              {['mp', 'cid', 'qrf'].map((u) => (
                <div key={u} className="units-overview-form-schema__card">
                  <div className="units-overview-form-schema__card-head">
                    <span className="units-overview-editor-assigner__unit">{UNIT_LABEL_SHORT[u]}</span>
                    <button
                      type="button"
                      className="admin-table-btn admin-table-btn--primary"
                      disabled={savingFormSchema === u}
                      onClick={() => handleSaveFormSchema(u)}
                    >
                      {savingFormSchema === u ? 'Zapisywanie…' : 'Zapisz szablon'}
                    </button>
                  </div>
                  <textarea
                    className="units-overview-form-schema__textarea admin-modal__input"
                    spellCheck={false}
                    value={formSchemaDrafts[u] || ''}
                    onChange={(e) => setFormSchemaDrafts((prev) => ({ ...prev, [u]: e.target.value }))}
                    rows={16}
                    aria-label={`Szablon JSON podania ${u}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="units-overview-grid">
          {UNITS_OVERVIEW_PANELS.map(({ unit, heading, sub, emoji }) => {
            const block = units[unit] || {};
            const s = block.stats || {};
            const isAdmin = block.isAdmin;
            const bl = block.blacklist;

            return (
              <section key={unit} className="units-overview-panel">
                <div className="units-overview-panel__head">
                  <span className="units-overview-panel__emoji" aria-hidden>{emoji}</span>
                  <div className="units-overview-panel__titles">
                    <h2>{heading}</h2>
                    {sub && <p className="units-overview-panel__sub">{sub}</p>}
                  </div>
                  {isAdmin && <span className="units-overview-admin-pill">Admin</span>}
                </div>

                {bl && (
                  <div className="units-overview-blacklist">
                    Twoje konto — blacklista ({bl.scope || '—'}): {bl.reason}
                  </div>
                )}

                <div className="units-overview-stats">
                  <p className="units-overview-stats__scope">W całej jednostce</p>
                  <StatLine label="Podania łącznie" value={s.applications ?? 0} />
                  <StatLine label="· oczekujące" value={s.applications_pending ?? 0} />
                  <StatLine label="· zaakceptowane" value={s.applications_accepted ?? 0} />
                  <StatLine label="· odrzucone" value={s.applications_rejected ?? 0} />
                  <StatLine label="Skargi łącznie" value={s.complaints ?? 0} />
                  <StatLine label="· oczekujące" value={s.complaints_pending ?? 0} />
                </div>

                <nav className="units-overview-links" aria-label={`Skróty ${heading}`}>
                  <Link to={`/${unit}/dashboard`}>Dashboard</Link>
                  <Link to={`/${unit}/application`}>Podanie</Link>
                  <Link to={`/${unit}/complaint`}>Skarga</Link>
                  {isAdmin && (
                    <Link to={`/${unit}/admin`} className="units-overview-links--admin">
                      Panel admin
                    </Link>
                  )}
                </nav>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
